import { createFileRoute, Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { PageHero } from '@/components/layout/PageHero';
import { FAQ_CATEGORIES } from '@/features/faq/content';
import { FaqCategoryCard } from '@/features/faq/components/FaqCategoryCard';

export const Route = createFileRoute('/faq')({
  component: FaqPage,
});

/**
 * FAQ page — static, curated content from features/faq/content.ts (no
 * network). A jump nav of category pills scrolls to each section card;
 * the footer card hands off to Help & Support for anything not covered.
 */
export function FaqPage() {
  const jumpTo = (id: string) => {
    // The AppShell content column is the scroll container, so scrolling the
    // section into view (not window.scrollTo) is what actually moves it.
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <PageHero
        title="Frequently Asked Questions"
        subtitle="Quick answers about the platform, the AI agents, and how your data is handled."
      />
      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20 space-y-6">
        {/* Featured onboarding tour — the video ships in the app bundle
            (public/videos/, same-origin), so no CSP or backend change. */}
        <section className="bg-white border border-line rounded-2xl p-6 sm:p-7 shadow-card">
          <h2 className="text-[15px] font-semibold text-ink m-0">
            Platform Setup &amp; AI Workflow Tour
          </h2>
          <p className="text-[14px] text-muted mt-1 mb-4">
            A guided walkthrough from company setup to your first agent run.
          </p>
          <video
            controls
            preload="metadata"
            aria-label="Platform Setup and AI Workflow Tour video"
            className="w-full aspect-video rounded-xl border border-line bg-navy-deep"
          >
            <source src="/videos/regara-platform-tour.mp4" type="video/mp4" />
          </video>
        </section>

        {/* Category jump nav — pills scroll to the matching section card. */}
        <nav
          aria-label="FAQ categories"
          className="bg-white border border-line rounded-2xl p-5 sm:p-6 shadow-card"
        >
          <h2 className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted m-0">
            Browse by topic
          </h2>
          <div className="flex flex-wrap gap-2 mt-3">
            {FAQ_CATEGORIES.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => jumpTo(category.id)}
                className="rounded-full border border-line bg-white px-3.5 py-1.5 text-[13px] font-medium text-navy cursor-pointer transition-colors duration-150 hover:bg-navy/5 hover:border-navy/30"
              >
                {category.title}
              </button>
            ))}
          </div>
        </nav>

        {FAQ_CATEGORIES.map((category) => (
          <FaqCategoryCard key={category.id} category={category} />
        ))}

        {/* Handoff to support for anything the FAQ doesn't cover. */}
        <section className="bg-white border border-line rounded-2xl p-6 sm:p-7 shadow-card flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-[15px] font-semibold text-ink m-0">
              Didn&rsquo;t find your answer?
            </h2>
            <p className="text-[14px] text-muted mt-1 mb-0">
              Send us a message and our team will get back to you by email.
            </p>
          </div>
          <Button asChild variant="brand" className="shrink-0 self-start sm:self-auto">
            <Link to="/support">Contact support</Link>
          </Button>
        </section>
      </main>
    </>
  );
}
